/**
 * Admin layout script class
 *
 * @package    Layouts
 * @author     Vadym Karpenko <vkarpenko@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */

import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classname';
import { withStyles } from '@material-ui/core/styles';
import SettingsIcon from 'Layouts/Layout/images/settings-icon.png';

import { Switch, Route, Redirect } from 'react-router-dom';

import styles from './styles.jsx';

import { unAuthorize } from 'Actions/auth';
import Sidebar from 'Common/Sidebar/Sidebar';
import { authenticatedPageDecorator, aclDecorator } from 'Decorators';
import UserCreate from 'Layouts/UserCreate';
import UserEdit from 'Layouts/UserEdit';
import UsersList from 'Layouts/UsersList';
import Profile from 'Layouts/Profile';
import JournalsList from 'Layouts/JournalsList';
import Journal from 'Layouts/Journal';
import Submission from 'Layouts/Submission';
import SubmissionManagement from 'Layouts/SubmissionManagement';
import SubmissionsList from 'Layouts/SubmissionsList';
import { ROLE_ADMIN, ROLE_CHIEF_EDITORIAL, ROLE_EDITORIAL, ROLE_EDITOR } from 'Constants';
import USER from 'Layouts/Layout/images/ic-user.svg';
import JOURNAL from 'Layouts/Layout/images/ic-ju.svg';
import SUBMISSION from 'Layouts/Layout/images/ic-submi.svg';
import INVITE from 'Layouts/Layout/images/invitation-icon.png';
import Minimize from 'Common/Header/Minimize';
import Settings from 'Layouts/Settings';
import { PrivacyPolicy, Contacts, AboutTheSystem, FAQ } from 'Layouts/StaticPages';
import SettingsInvitationsList from 'Layouts/Settings/containers/SettingsInvitationsList';

@withStyles(styles)
@authenticatedPageDecorator()
@aclDecorator()
@connect(
    null,
    dispatch => ({
        unAuthorize: () => dispatch(unAuthorize()),
    })
)
export default class AdminLayout extends PureComponent {
    /**
     * @type {Object}
     */
    static propTypes = {
        unAuthorize: PropTypes.func.isRequired,
        classes: PropTypes.object.isRequired,
        is: PropTypes.func.isRequired,
        isOneOf: PropTypes.func.isRequired,
        isNotOneOf: PropTypes.func.isRequired,
    };

    /**
     * @type {Object}
     */
    static contextTypes = {
        intl: PropTypes.object.isRequired,
    };

    /**
     * @type {Object}
     */
    state = {
        mobileOpen: false,
        miniActive: false,
    };

    sidebarMinimize = () => {
        this.setState(prevState => ({
            miniActive: !prevState.miniActive,
        }));
    };

    handleDrawerToggle = () => {
        this.setState(prevState => ({
            mobileOpen: !prevState.mobileOpen,
        }));
    };

    logout = event => {
        event.preventDefault();

        this.props.unAuthorize();
    };

    render() {
        const { miniActive, mobileOpen } = this.state;
        const { classes, is, isNotOneOf, ...rest } = this.props;
        const mainPanel =
            classes.mainPanel +
            ' ' +
            cx({
                [classes.mainPanelSidebarMini]: this.state.miniActive,
                [classes.mainPanelWithPerfectScrollbar]: navigator.platform.indexOf('Win') > -1,
            });

        const routes = [
            {
                hidden: true,
                exact: true,
                path: '/profile/:tab?',
                name: 'profile',
                component: Profile,
            },
            {
                exact: true,
                path: '/',
                name: 'submissions',
                component: SubmissionsList,
                image: SUBMISSION,
            },
            {
                hidden: true,
                exact: true,
                path: '/journals/create',
                name: 'journals',
                component: Journal,
            },
            {
                hidden: true,
                exact: true,
                path: '/journals/:id',
                name: 'journals',
                component: Journal,
            },
            {
                hidden: !is(ROLE_ADMIN),
                exact: true,
                path: '/journals',
                name: 'journals',
                image: JOURNAL,
                component: JournalsList,
            },
            {
                hidden: true,
                exact: true,
                path: '/users/create',
                name: 'users',
                component: UserCreate,
            },
            {
                hidden: true,
                exact: true,
                path: '/users/:id/:tab?',
                name: 'users',
                component: UserEdit,
            },
            {
                hidden: !is(ROLE_ADMIN),
                exact: true,
                path: '/users',
                name: 'users',
                image: USER,
                component: UsersList,
            },
            {
                hidden: true,
                exact: true,
                path: '/submissions/create',
                name: 'submission_create',
                component: Submission,
            },
            {
                hidden: true,
                exact: true,
                path: '/submissions/:id/:tab?',
                name: 'submissions',
                component: SubmissionManagement,
            },
            {
                hidden: isNotOneOf([ROLE_ADMIN, ROLE_EDITORIAL, ROLE_CHIEF_EDITORIAL, ROLE_EDITOR]),
                path: '/invitations',
                name: 'invitations',
                image: INVITE,
                component: SettingsInvitationsList,
            },
            {
                hidden: true,
                path: '/settings/:tab?/:id?',
                name: 'settings_hidden',
                component: Settings,
            },
            {
                hidden: !is(ROLE_ADMIN),
                path: '/settings',
                name: 'settings',
                component: Settings,
                image: SettingsIcon,
            },
            {
                footer: true,
                path: '/privacy-policy',
                name: 'privacy_policy',
                component: PrivacyPolicy,
            },
            {
                footer: true,
                path: '/contacts',
                name: 'contacts',
                component: Contacts,
            },
            {
                footer: true,
                path: '/about-the-system',
                name: 'about_the_system',
                component: AboutTheSystem,
            },
            {
                footer: true,
                path: '/faq',
                name: 'faq',
                component: FAQ,
            },
            { redirect: true, path: '*', pathTo: '/404' },
        ];

        return (
            <div>
                <Sidebar
                    routes={routes}
                    handleDrawerToggle={this.handleDrawerToggle}
                    open={mobileOpen}
                    color="light"
                    bgColor="black"
                    miniActive={miniActive}
                    logout={this.logout}
                    classes={{ logo: 'sidebar-header-block' }}
                    {...rest}
                />

                <div className={mainPanel}>
                    <Minimize sidebarMinimize={this.sidebarMinimize} miniActive={miniActive} classes={{sidebarMinimize: 'minimize-btn-wrapper'}} />

                    <div className={classes.content}>
                        <div className={classes.container}>
                            <Switch>
                                {routes.map((prop, key) => {
                                    if (prop.redirect) {
                                        return <Redirect from={prop.path} to={prop.pathTo} key={key} />;
                                    }
                                    if (prop.collapse) {
                                        return prop.views.map((prop, key) => {
                                            return (
                                                <Route
                                                    exact={prop.exact}
                                                    path={prop.path}
                                                    component={prop.component}
                                                    key={key}
                                                />
                                            );
                                        });
                                    }
                                    return (
                                        <Route
                                            exact={prop.exact}
                                            path={prop.path}
                                            component={prop.component}
                                            key={key}
                                        />
                                    );
                                })}
                            </Switch>
                        </div>
                        <div className="copyright-mk">Developed by <a href="https://mindk.com/" rel='noreferrer noopener' target="_blank">MindK</a></div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/layouts/AdminLayout/AdminLayout.jsx